import React, { useCallback, useState } from 'react';

import { Box } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import ActionButton from 'components/action-button';
import { MobileSpacer } from 'components/mobile-spacer';
import Modal, { ModalContent, ModalHeading } from 'components/modal';
import { usePrepaidsBalance } from 'hooks/prepaid/use-prepaids-balance';
import useErrorModal from 'hooks/use-error-modal';
import { AddGiftCardInput } from 'pages/cart/payment/add-gift-card-input';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLogger } from 'state/logger';
import { CARD_ALIAS_TRANSLATION_ID } from 'utils/gift-card/constants';
import { relativeRoutes } from 'utils/routing';

import { GIFT_CARD_NUMBER_TRANSLATION_ID } from '../../add-inline-gift-card/constants';
import GiftCardImage from '../gift-card-image';
import { BalanceDisclaimer, FormContainer } from '../styled';
import { useCardNumber } from '../use-giftcard-number';

const TransferGiftCardModal: React.FC<{
  onCardTransfered: () => void;
}> = () => {
  const [isLoading, setIsLoading] = useState(false);
  const logger = useLogger();
  const { formatMessage } = useIntl();
  const { giftCardNumber, errors, isValidGiftCardNumber, handleCardNumberChange } = useCardNumber();
  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Vaulting Gift Card',
  });
  const { region } = useLocale();
  const navigate = useNavigate();
  const { getPrepaidsBalance } = usePrepaidsBalance({ region });
  const location = useLocation();
  const enablePrepaidDigitalCardCreation = useFlag(
    LaunchDarklyFlag.ENABLE_PREPAID_DIGITAL_CARD_CREATION
  );

  const transferCard = useCallback(
    async event => {
      event.preventDefault();
      setIsLoading(true);
      try {
        const { data } = await getPrepaidsBalance({ cardNumber: giftCardNumber });
        const transferCardBalance = data?.prepaidsBalance.currentBalance;
        const removeCurrentRoute = location.pathname.replace(relativeRoutes.transferGiftCard, '');
        // Callback
        navigate(`${removeCurrentRoute}/${relativeRoutes.successTransferGiftCard}`, {
          state: { transferCardBalance, cardNumber: giftCardNumber },
        });
      } catch (error) {
        openErrorDialog({ message: formatMessage({ id: 'giftCardAddingError' }), error });
        logger.error({ error, message: 'Error transfer gift card' });
      } finally {
        setIsLoading(false);
      }
    },
    [
      getPrepaidsBalance,
      giftCardNumber,
      location.pathname,
      navigate,
      openErrorDialog,
      formatMessage,
      logger,
    ]
  );

  return (
    <Modal onDismiss={() => navigate('../')}>
      <ModalContent>
        <ModalHeading id="modal-heading">
          {formatMessage(
            { id: 'transferBalanceFromPrepaidCard' },
            { cardAlias: formatMessage({ id: CARD_ALIAS_TRANSLATION_ID }) }
          )}
        </ModalHeading>
        <GiftCardImage />
        {enablePrepaidDigitalCardCreation && (
          <BalanceDisclaimer data-testid="gift-card-balance-disclaimer" aria-live="polite">
            {formatMessage({ id: 'giftCardBalanceDisclaimer' })}
          </BalanceDisclaimer>
        )}
        <FormContainer data-testid="transfer-giftcard-form" onSubmit={transferCard} noValidate>
          <AddGiftCardInput
            onChange={handleCardNumberChange}
            errorMessage={errors?.giftCardNumber}
            label={formatMessage({ id: GIFT_CARD_NUMBER_TRANSLATION_ID })}
            data-testid="transfer-giftcard-input"
          />
          <Box margin="1rem 0">
            <ActionButton
              fullWidth
              data-testid="transfer-now"
              disabled={!isValidGiftCardNumber}
              onClick={transferCard}
              isLoading={isLoading}
            >
              {formatMessage({ id: 'transfer' })}
            </ActionButton>
          </Box>
          <MobileSpacer />
        </FormContainer>
      </ModalContent>

      <ErrorDialog />
    </Modal>
  );
};

export default TransferGiftCardModal;
